// * 处方表单纯展示组件，无任何填写功能

import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import WapOnly from '@/components/Responsive/WapOnly';
import WebOnly from '@/components/Responsive/WebOnly';
import { useStore } from '@/providers/app-store-provider';
import { LocaleType } from '@/utils/constants';
import { CollapseArrowDownOutlined } from '@zeelool/icon';
import { useLocale, useTranslations } from 'next-intl';
import { useContext, useMemo } from 'react';
import { usePrescriptionStyle } from './usePrescriptionStyle';
import useResponsive from '@/hooks/useResponsive';

/** 处方示例数值（纯展示用） */
const PRESCRIPTION_SAMPLE_VALUES = {
  sphOd: 0.5,
  sphOs: 1.5,
  cylOd: -0.75,
  cylOs: -1.0,
  axisOd: 1,
  axisOs: 3,
  add: 1.5,
  prismH: 0.75,
  prismV: 0.5,
  pd: 62.5,
  // zero: 0,
  zeroDecimal: 0,
} as const;

function formatPrescriptionNumber(
  value: number,
  locale: string,
  opts?: { showPlus?: boolean; fractionDigits?: number },
): string {
  const str = opts?.fractionDigits != null ? value.toFixed(opts.fractionDigits) : String(value);
  let result = opts?.showPlus && value > 0 ? `+${str}` : str;
  // 德语站：小数点用逗号
  if (locale === 'de' || locale.startsWith('de-')) {
    result = result.replace('.', ',');
  }
  return result;
}

interface PrescriptionFormProps {
  className?: string;
  /** 选中的字段 */
  paramValue?: 'sph' | 'cyl' | 'axis' | 'add' | 'prism' | 'pd' | 'od/os';
}

export default function PrescriptionForm({ className = '', paramValue }: PrescriptionFormProps) {
  const t = useTranslations('prescriptionForm');
  const waiMode = useStore((state) => state.waiMode);
  const locale = useLocale() as LocaleType;
  const { deviceType } = useContext(ResponsiveContext);
  const { isMobile } = useResponsive();

  const {
    prescriptionFormStyle,
    formItemWidthStyle,
    prismFormItemWidthStyle,
    baseFormItemStyle,
    getCoverBlockClassName,
    grayColor,
    isShowPrismForm,
  } = usePrescriptionStyle({
    locale,
    deviceType,
    paramValue,
    waiMode,
  });

  // 根据当前站点语言格式化处方数值（德语站用 Intl 处理小数位等）
  const displayValues = useMemo(() => {
    const fmt = (v: number, opts?: { showPlus?: boolean; fractionDigits?: number }) =>
      formatPrescriptionNumber(v, locale, opts);
    const v = PRESCRIPTION_SAMPLE_VALUES;
    return {
      sphOd: fmt(v.sphOd, { showPlus: true, fractionDigits: 2 }),
      sphOs: fmt(v.sphOs, { showPlus: true, fractionDigits: 2 }),
      cylOd: fmt(v.cylOd, { showPlus: true, fractionDigits: 2 }),
      cylOs: fmt(v.cylOs, { showPlus: true, fractionDigits: 2 }),
      axisOd: fmt(v.axisOd, { fractionDigits: 0 }),
      axisOs: fmt(v.axisOs, { fractionDigits: 0 }),

      prismH: fmt(v.prismH, { fractionDigits: 2 }),
      prismV: fmt(v.prismV, { fractionDigits: 2 }),
      add: fmt(v.add, { showPlus: true, fractionDigits: 2 }),
      pd: fmt(v.pd, { fractionDigits: 2 }),
      // zero: fmt(v.zero, { fractionDigits: 0 }),
      zeroDecimal: fmt(v.zeroDecimal, { fractionDigits: 2 }),
    };
  }, [locale]);

  // 展示值的通用类名
  const displayValueClass =
    'w-full h-full flex items-center font-br justify-center text-14 text-[#282828] notranslate';
  const collapseIconClass = isMobile ? 'text-14 ml-[2px]' : 'text-16 absolute right-16';

  return (
    <div className="relative">
      <WebOnly>
        {!isShowPrismForm && (
          <div className={`${prescriptionFormStyle({ locale })} ${className}`}>
            {/* SPH CYL AXIS 表单 */}
            <div className="base-form rounded-6 border border-[#D4D4D4] overflow-hidden notranslate">
              {/* 表头，蓝色背景 */}
              <div className="row flex box-border justify-start text-center bg-[#edf2fc]">
                <div
                  className={`title w-[128px] h-48 border-r flex items-center justify-center font-bb text-14 border-[#D4D4D4] `}
                >
                  <span>&nbsp;</span>
                </div>
                <div className={`${baseFormItemStyle}`}>
                  <span>{t('fieldLabels.sph')}</span>
                </div>
                <div className={`${baseFormItemStyle}`}>
                  <span>{t('fieldLabels.cyl')}</span>
                </div>
                <div className={`${baseFormItemStyle}`}>
                  <span>{t('fieldLabels.axis')}</span>
                </div>

                <div className={`${baseFormItemStyle} border-r-0`}>
                  <span>{t('fieldLabels.add')}</span>
                </div>
              </div>

              {/* 表单内容 右眼 */}
              <div className="row flex justify-start text-center ">
                <div
                  className={`title w-[128px] h-48 border-r border-t border-[#D4D4D4] pl-12 flex items-center justify-start font-bb text-14 text-[#282828] bg-[#edf2fc] `}
                >
                  <span className="text-left">
                    {t('eyeLabels.od')} <br />
                    <span className={`font-br text-12 ${grayColor}`}> {t('eyeLabels.odHint')}</span>
                  </span>
                </div>
                <div
                  className={`title ${formItemWidthStyle({ locale })} h-48 border-r border-t flex items-center justify-center font-bb text-14 text-[#282828] border-[#D4D4D4] bg-white relative`}
                >
                  <div className={displayValueClass}>
                    {displayValues.sphOd}
                    <CollapseArrowDownOutlined className={collapseIconClass} />
                  </div>
                </div>
                <div
                  className={`title ${formItemWidthStyle({ locale })} h-48 border-r border-t flex items-center justify-center font-bb text-14 border-[#D4D4D4] bg-white relative`}
                >
                  <div className={displayValueClass}>
                    {displayValues.cylOd}
                    <CollapseArrowDownOutlined className={collapseIconClass} />
                  </div>
                </div>
                <div
                  className={`title ${formItemWidthStyle({ locale })} h-48 border-r border-t flex items-center justify-center font-bb text-14 border-[#D4D4D4] bg-white relative`}
                >
                  <div className={displayValueClass}>
                    {displayValues.axisOd}
                    <CollapseArrowDownOutlined className={collapseIconClass} />
                  </div>
                </div>
                <div
                  className={`title ${prismFormItemWidthStyle({ locale })} h-48 flex items-center border-t border-[#D4D4D4] justify-center font-bb text-14 bg-white relative`}
                >
                  <div className={displayValueClass}>
                    {displayValues.add}
                    <CollapseArrowDownOutlined className={collapseIconClass} />
                  </div>
                </div>
              </div>

              {/* 表单内容 左眼 */}
              <div className="row flex justify-start text-center ">
                <div
                  className={`title w-[128px] h-48 border-r border-t pl-12 flex items-center justify-start font-bb text-14 text-[#282828] border-[#D4D4D4] bg-[#edf2fc] `}
                >
                  <span className="text-left">
                    {t('eyeLabels.os')} <br />
                    <span className={`font-br text-12 ${grayColor}`}> {t('eyeLabels.osHint')}</span>
                  </span>
                </div>
                <div
                  className={`title ${formItemWidthStyle({ locale })} h-48 border-r border-t flex items-center justify-center font-bb text-14 text-[#282828] border-[#D4D4D4] bg-white relative`}
                >
                  <div className={displayValueClass}>
                    {displayValues.sphOs}
                    <CollapseArrowDownOutlined className={collapseIconClass} />
                  </div>
                </div>
                <div
                  className={`title ${formItemWidthStyle({ locale })} h-48 border-r border-t flex items-center justify-center font-bb text-14 border-[#D4D4D4] bg-white relative`}
                >
                  <div className={displayValueClass}>
                    {displayValues.cylOs}
                    <CollapseArrowDownOutlined className={collapseIconClass} />
                  </div>
                </div>
                <div
                  className={`title ${formItemWidthStyle({ locale })} h-48 border-r border-t flex items-center justify-center font-bb text-14 border-[#D4D4D4] bg-white relative`}
                >
                  <div className={displayValueClass}>
                    {displayValues.axisOs}
                    <CollapseArrowDownOutlined className={collapseIconClass} />
                  </div>
                </div>
                <div
                  className={`title ${prismFormItemWidthStyle({ locale })} h-48 flex items-center border-t border-[#D4D4D4] justify-center font-bb text-14 bg-white relative`}
                >
                  <div className={displayValueClass}>
                    {displayValues.add}
                    <CollapseArrowDownOutlined className={collapseIconClass} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {!isShowPrismForm && (
          <div className={`${prescriptionFormStyle({ locale })} ${className}`}>
            {/* PD 部分 */}
            <div className="w-full mt-14 flex items-center">
              <div
                className={`title w-[128px] h-48 border-r font-bb bg-[#edf2fc] rounded-l-6 border text-14 text-[#282828] border-[#D4D4D4] flex items-center notranslate `}
              >
                <span className="pl-12 text-left">
                  {t('fieldLabels.pd')} <br />
                  <span className={`font-br text-12 ${grayColor}`}> {t('fieldLabels.pdHint')}</span>
                </span>
              </div>
              <div
                className={`${formItemWidthStyle({ locale })} h-48 rounded-r-6 bg-white border-t border-r border-b border-[#D4D4D4] flex items-center justify-center text-14 text-[#282828] notranslate relative`}
              >
                <div className={displayValueClass}>
                  {displayValues.pd}
                  <CollapseArrowDownOutlined className={collapseIconClass} />
                </div>
              </div>
            </div>
          </div>
        )}

        {isShowPrismForm && (
          <div className={`${prescriptionFormStyle({ locale })} ${className} mt-[14px]`}>
            <div className="w-full">
              <div className="base-form bg-[#edf2fc] rounded-6 border border-[#D4D4D4] overflow-hidden notranslate">
                <div className="row flex justify-between text-center notranslate">
                  <div
                    className={`title w-[128px] h-48 border-r flex items-center justify-center font-bb text-14 border-[#D4D4D4] `}
                  >
                    <span>&nbsp;</span>
                  </div>
                  <div
                    className={`title ${prismFormItemWidthStyle({ locale })} h-48 border-r flex items-center justify-center font-bb text-14 text-[#282828] border-[#D4D4D4] `}
                  >
                    <span
                      dangerouslySetInnerHTML={{
                        __html: t('fieldLabels.prismHorizontal'),
                      }}
                    />
                  </div>
                  <div
                    className={`title ${prismFormItemWidthStyle({ locale })} h-48 border-r flex items-center justify-center font-bb text-14 text-[#282828] border-[#D4D4D4] `}
                  >
                    <span
                      dangerouslySetInnerHTML={{
                        __html: t('fieldLabels.baseDirection'),
                      }}
                    />
                  </div>
                  <div
                    className={`title ${prismFormItemWidthStyle({ locale })} h-48 border-r flex items-center justify-center font-bb text-14 text-[#282828] border-[#D4D4D4] `}
                  >
                    <span
                      dangerouslySetInnerHTML={{
                        __html: t('fieldLabels.prismVertical'),
                      }}
                    />
                  </div>
                  <div
                    className={`title ${prismFormItemWidthStyle({ locale })} h-48 flex items-center justify-center font-bb text-14 text-[#282828] `}
                  >
                    <span
                      dangerouslySetInnerHTML={{
                        __html: t('fieldLabels.baseDirection'),
                      }}
                    />
                  </div>
                </div>

                {/* OD 右眼 */}
                <div className="row flex justify-between text-center">
                  <div
                    className={`title w-[128px] h-48 border-r border-t flex items-center justify-start font-bb text-14 text-[#282828] border-[#D4D4D4] `}
                  >
                    <span className="pl-12 text-left">
                      {t('fieldLabels.od')} <br />
                      <span className={`font-br text-12 ${grayColor}`}>
                        {t('fieldLabels.right')}
                      </span>
                    </span>
                  </div>
                  <div
                    className={`title ${prismFormItemWidthStyle({ locale })} h-48 bg-white border-r border-t flex items-center justify-center font-bb text-14 text-[#282828] border-[#D4D4D4] relative`}
                  >
                    <div className={displayValueClass}>
                      {displayValues.prismH}
                      <CollapseArrowDownOutlined className={collapseIconClass} />
                    </div>
                  </div>
                  <div
                    className={`title ${prismFormItemWidthStyle({ locale })} h-48 bg-white border-r border-t flex items-center justify-center font-bb text-14 border-[#D4D4D4] relative`}
                  >
                    <div className={displayValueClass}>
                      {t('fieldLabels.Out')}
                      <CollapseArrowDownOutlined className={collapseIconClass} />
                    </div>
                  </div>
                  <div
                    className={`title ${prismFormItemWidthStyle({ locale })} h-48 bg-white border-r border-t flex items-center justify-center font-bb text-14 border-[#D4D4D4] relative`}
                  >
                    <div className={displayValueClass}>
                      {displayValues.prismV}
                      <CollapseArrowDownOutlined className={collapseIconClass} />
                    </div>
                  </div>
                  <div
                    className={`title ${prismFormItemWidthStyle({ locale })} h-48 bg-white border-t flex items-center justify-center font-bb text-14 border-[#D4D4D4] relative`}
                  >
                    <div className={displayValueClass}>
                      {t('fieldLabels.Up')}
                      <CollapseArrowDownOutlined className={collapseIconClass} />
                    </div>
                  </div>
                </div>

                {/* OS 左眼 */}
                <div className="row flex justify-between text-center">
                  <div
                    className={`title w-[128px] h-48 border-r border-t flex items-center justify-start font-bb text-14 text-[#282828] border-[#D4D4D4] `}
                  >
                    <span className="pl-12 text-left">
                      {t('fieldLabels.os')} <br />
                      <span className={`font-br text-12 ${grayColor}`}>
                        {t('fieldLabels.left')}
                      </span>
                    </span>
                  </div>
                  <div
                    className={`title ${prismFormItemWidthStyle({ locale })} h-48 bg-white border-r border-t flex items-center justify-center font-bb text-14 border-[#D4D4D4] relative`}
                  >
                    <div className={displayValueClass}>
                      {displayValues.zeroDecimal}
                      <CollapseArrowDownOutlined className={collapseIconClass} />
                    </div>
                  </div>
                  <div
                    className={`title ${prismFormItemWidthStyle({ locale })} h-48 bg-white border-r border-t flex items-center justify-center font-bb text-14 border-[#D4D4D4] relative`}
                  >
                    <div className={`bg-[#F4F4F4] text-[#d4d4d4] ${displayValueClass}`}>
                      {t('fieldLabels.select')}
                      <CollapseArrowDownOutlined className={collapseIconClass} />
                    </div>
                  </div>
                  <div
                    className={`title ${prismFormItemWidthStyle({ locale })} h-48 bg-white border-r border-t flex items-center justify-center font-bb text-14 border-[#D4D4D4] relative`}
                  >
                    <div className={displayValueClass}>
                      {displayValues.zeroDecimal}
                      <CollapseArrowDownOutlined className={collapseIconClass} />
                    </div>
                  </div>
                  <div
                    className={`title ${prismFormItemWidthStyle({ locale })} h-48 bg-white border-t flex items-center justify-center font-bb text-14 border-[#D4D4D4] relative`}
                  >
                    <div className={`bg-[#F4F4F4] text-[#d4d4d4] ${displayValueClass}`}>
                      {t('fieldLabels.select')}
                      <CollapseArrowDownOutlined className={collapseIconClass} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </WebOnly>

      <WapOnly>
        <div className={`w-full prescription-form-pc ${className}`}>
          {/* SPH CYL AXIS ADD 表单 */}
          {!isShowPrismForm && (
            <div className="base-form bg-[#edf2fc] rounded-6 border border-[#D4D4D4] overflow-hidden notranslate">
              <div className="row flex justify-between text-center notranslate">
                <div className="title w-1/5 min-w-0 h-48 border-r flex items-center justify-center font-bb text-14 border-[#D4D4D4]">
                  <span>&nbsp;</span>
                </div>
                <div className="title w-1/5 min-w-0 h-48 border-r flex items-center justify-center font-bb text-14 border-[#D4D4D4]">
                  <span>{t('fieldLabels.sph')}</span>
                </div>
                <div className="title w-1/5 min-w-0 h-48 border-r flex items-center justify-center font-bb text-14 border-[#D4D4D4]">
                  <span>{t('fieldLabels.cyl')}</span>
                </div>
                <div className="title w-1/5 min-w-0 h-48 border-r flex items-center justify-center font-bb text-14 border-[#D4D4D4]">
                  <span>{t('fieldLabels.axis')}</span>
                </div>
                <div className="title w-1/5 min-w-0 h-48 flex items-center justify-center font-bb text-14 border-[#D4D4D4]">
                  <span>{t('fieldLabels.add')}</span>
                </div>
              </div>

              {/* 右眼 OD */}
              <div className="row flex justify-between text-center">
                <div className="title w-1/5 min-w-0 h-48 border-r border-t flex items-center justify-start font-bb text-14 border-[#D4D4D4] pl-12">
                  <span className="text-left">
                    <span className="notranslate">{t('fieldLabels.od')}</span> <br />
                    <span className={`font-br text-12 ${grayColor}`}>{t('fieldLabels.right')}</span>
                  </span>
                </div>
                <div className="title w-1/5 min-w-0 h-48 bg-white border-r border-t flex items-center justify-center text-14 border-[#D4D4D4] relative">
                  <div className={displayValueClass}>
                    {displayValues.sphOd}
                    <CollapseArrowDownOutlined className={collapseIconClass} />
                  </div>
                </div>
                <div className="title w-1/5 min-w-0 h-48 bg-white border-r border-t flex items-center justify-center text-14 border-[#D4D4D4] relative">
                  <div className={displayValueClass}>
                    {displayValues.cylOd}
                    <CollapseArrowDownOutlined className={collapseIconClass} />
                  </div>
                </div>
                <div className="title w-1/5 min-w-0 h-48 bg-white border-r border-t flex items-center justify-center text-14 border-[#D4D4D4] relative">
                  <div className={displayValueClass}>
                    {displayValues.axisOd}
                    <CollapseArrowDownOutlined className={collapseIconClass} />
                  </div>
                </div>
                <div className="title w-1/5 min-w-0 h-48 bg-white border-t flex items-center justify-center text-14 border-[#D4D4D4] relative">
                  <div className={displayValueClass}>
                    {displayValues.add}
                    <CollapseArrowDownOutlined className={collapseIconClass} />
                  </div>
                </div>
              </div>

              {/* 左眼 OS */}
              <div className="row flex justify-between text-center">
                <div className="title w-1/5 min-w-0 h-48 border-r border-t flex items-center justify-start font-bb text-14 border-[#D4D4D4] pl-12">
                  <span className="text-left">
                    <span className="notranslate">{t('fieldLabels.os')}</span> <br />
                    <span className={`font-br text-12 ${grayColor}`}>{t('fieldLabels.left')}</span>
                  </span>
                </div>
                <div className="title w-1/5 min-w-0 h-48 bg-white border-r border-t flex items-center justify-center text-14 border-[#D4D4D4] relative">
                  <div className={displayValueClass}>
                    {displayValues.sphOs}
                    <CollapseArrowDownOutlined className={collapseIconClass} />
                  </div>
                </div>
                <div className="title w-1/5 min-w-0 h-48 bg-white border-r border-t flex items-center justify-center text-14 border-[#D4D4D4] relative">
                  <div className={displayValueClass}>
                    {displayValues.cylOs}
                    <CollapseArrowDownOutlined className={collapseIconClass} />
                  </div>
                </div>
                <div className="title w-1/5 min-w-0 h-48 bg-white border-r border-t flex items-center justify-center text-14 border-[#D4D4D4] relative">
                  <div className={displayValueClass}>
                    {displayValues.axisOs}
                    <CollapseArrowDownOutlined className={collapseIconClass} />
                  </div>
                </div>
                <div className="title w-1/5 min-w-0 h-48 bg-white border-t flex items-center justify-center text-14 border-[#D4D4D4] relative">
                  <div className={displayValueClass}>
                    {displayValues.add}
                    <CollapseArrowDownOutlined className={collapseIconClass} />
                  </div>
                </div>
              </div>
            </div>
          )}
          {/* PD 部分 */}
          {!isShowPrismForm && (
            <>
              <div className="w-full mt-10 flex items-center notranslate">
                <div className="title w-1/5 h-48 border-r font-bb bg-[#edf2fc] rounded-l-6 border text-14 border-[#D4D4D4] flex items-center ">
                  <span className="pl-12 text-left notranslate">{t('fieldLabels.pd')}</span>
                </div>
                <div className="w-1/5 h-48 rounded-r-6 bg-white border-t border-r border-b border-[#D4D4D4] flex items-center justify-center text-14 relative">
                  <div className={displayValueClass}>
                    {displayValues.pd}
                    <CollapseArrowDownOutlined className={collapseIconClass} />
                  </div>
                </div>
              </div>
            </>
          )}
          {/* {!isShowPrismForm && <div className="w-full h-1 border-t border-[#d0d4db] mb-4 mt-16" />} */}

          <section style={{ display: isShowPrismForm ? 'block' : 'none' }}>
            {/* <div className="mt-[14px] mb-[10px] flex items-center">
              <div className="text-16 font-bb">{t('buttons.prism')}</div>
            </div> */}

            <div className="base-form bg-[#edf2fc] rounded-6 border border-[#D4D4D4] mt-2 overflow-hidden notranslate">
              {/* 表头：4 个字段并排 */}
              <div className="row flex text-center">
                <div
                  className={`title w-1/5 min-w-0 h-48 border-r flex items-center justify-center font-bb text-14 border-[#D4D4D4]`}
                >
                  <span>&nbsp;</span>
                </div>
                <div
                  className={`title w-1/5 min-w-0 h-48 border-r flex items-center justify-center font-bb text-14 border-[#D4D4D4]`}
                >
                  <span dangerouslySetInnerHTML={{ __html: t('fieldLabels.prismHorizontal') }} />
                </div>
                <div
                  className={`title w-1/5 min-w-0 h-48 border-r flex items-center justify-center font-bb text-14 border-[#D4D4D4]`}
                >
                  <span dangerouslySetInnerHTML={{ __html: t('fieldLabels.baseDirection') }} />
                </div>
                <div
                  className={`title w-1/5 min-w-0 h-48 border-r flex items-center justify-center font-bb text-14 border-[#D4D4D4]`}
                >
                  <span dangerouslySetInnerHTML={{ __html: t('fieldLabels.prismVertical') }} />
                </div>
                <div
                  className={`title w-1/5 min-w-0 h-48 flex items-center justify-center font-bb text-14 border-[#D4D4D4]`}
                >
                  <span dangerouslySetInnerHTML={{ __html: t('fieldLabels.baseDirection') }} />
                </div>
              </div>

              {/* OD 右眼 - 4 列数据并排 */}
              <div className="row flex text-center">
                <div
                  className={`title w-1/5 min-w-0 h-48 border-r border-t flex items-center justify-start font-bb text-14 border-[#D4D4D4] pl-12`}
                >
                  <span className="text-left">
                    {t('fieldLabels.od')} <br />
                    <span className={`font-br text-12 ${grayColor}`}>{t('fieldLabels.right')}</span>
                  </span>
                </div>
                <div
                  className={`title w-1/5 min-w-0 h-48 bg-white border-r border-t flex items-center justify-center text-14 border-[#D4D4D4] relative`}
                >
                  <div className={displayValueClass}>
                    {displayValues.prismH}
                    <CollapseArrowDownOutlined className={collapseIconClass} />
                  </div>
                </div>
                <div
                  className={`title w-1/5 min-w-0 h-48 bg-white border-r border-t flex items-center justify-center text-14 border-[#D4D4D4] relative`}
                >
                  <div className={displayValueClass}>
                    {t('fieldLabels.Out')}
                    <CollapseArrowDownOutlined className={collapseIconClass} />
                  </div>
                </div>
                <div
                  className={`title w-1/5 min-w-0 h-48 bg-white border-r border-t flex items-center justify-center text-14 border-[#D4D4D4] relative`}
                >
                  <div className={displayValueClass}>
                    {displayValues.prismV}
                    <CollapseArrowDownOutlined className={collapseIconClass} />
                  </div>
                </div>
                <div
                  className={`title w-1/5 min-w-0 h-48 bg-white border-t flex items-center justify-center text-14 border-[#D4D4D4] relative`}
                >
                  <div className={displayValueClass}>
                    {t('fieldLabels.Up')}
                    <CollapseArrowDownOutlined className={collapseIconClass} />
                  </div>
                </div>
              </div>

              {/* OS 左眼 - 4 列数据并排 */}
              <div className="row flex text-center">
                <div
                  className={`title w-1/5 min-w-0 h-48 border-r border-t flex items-center justify-start font-bb text-14 border-[#D4D4D4] pl-12`}
                >
                  <span className="text-left">
                    {t('fieldLabels.os')} <br />
                    <span className={`font-br text-12 ${grayColor}`}>{t('fieldLabels.left')}</span>
                  </span>
                </div>
                <div
                  className={`title w-1/5 min-w-0 h-48 bg-white border-r border-t flex items-center justify-center text-14 border-[#D4D4D4] relative`}
                >
                  <div className={displayValueClass}>
                    {displayValues.zeroDecimal}
                    <CollapseArrowDownOutlined className={collapseIconClass} />
                  </div>
                </div>
                <div
                  className={`title w-1/5 min-w-0 h-48 bg-white border-r border-t flex items-center justify-center text-14 border-[#D4D4D4] relative`}
                >
                  <div className={`bg-[#F4F4F4] text-[#d4d4d4] ${displayValueClass}`}>
                    {t('fieldLabels.select')}
                    <CollapseArrowDownOutlined className={collapseIconClass} />
                  </div>
                </div>
                <div
                  className={`title w-1/5 min-w-0 h-48 bg-white border-r border-t flex items-center justify-center text-14 border-[#D4D4D4] relative`}
                >
                  <div className={displayValueClass}>
                    {displayValues.zeroDecimal}
                    <CollapseArrowDownOutlined className={collapseIconClass} />
                  </div>
                </div>
                <div
                  className={`title w-1/5 min-w-0 h-48 bg-white border-t flex items-center justify-center text-14 border-[#D4D4D4] relative`}
                >
                  <div className={`bg-[#F4F4F4] text-[#d4d4d4] ${displayValueClass}`}>
                    {t('fieldLabels.select')}
                    <CollapseArrowDownOutlined className={collapseIconClass} />
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </WapOnly>

      {/* 悬浮框 */}
      <div className={getCoverBlockClassName()}>{/* 悬浮框内容可以在这里添加 */}</div>
    </div>
  );
}
